/**
 * Ein ZIP-Archiv OHNE Kompression („stored") — genug, um mehrere Dokumente
 * in EINEM Download auszuliefern (PO 28.09.2026: „Herunterladen der Dokumente
 * im Vorgang").
 *
 * Warum selbst gebaut und nicht `jszip`/`fflate`: Das Repo hat keine
 * ZIP-Bibliothek als Abhängigkeit, und fürs Bündeln reicht das Speichern ohne
 * Packen — PDFs, Bilder und Office-Dateien sind ohnehin schon komprimiert.
 * Das Format ist PKWARE APPNOTE 6.3, Abschnitte 4.3.7 (Local File Header),
 * 4.3.12 (Central Directory) und 4.3.16 (End of Central Directory). Namen
 * stehen als UTF-8 mit gesetztem Bit 11 — sonst zeigt der Windows-Explorer
 * „Prüfung.pdf" als Zeichensalat.
 *
 * Grenze: kein ZIP64. Über 4 GB bzw. 65 535 Einträge wirft {@link baueZip};
 * im Vorgang ist das weit weg.
 */

export type ZipEintrag = { name: string; daten: Uint8Array; datum?: Date };

const CRC_TABELLE = (() => {
    const t = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        t[n] = c >>> 0;
    }
    return t;
})();

export function crc32(daten: Uint8Array): number {
    let c = 0xffffffff;
    for (let i = 0; i < daten.length; i++) c = CRC_TABELLE[(c ^ daten[i]!) & 0xff]! ^ (c >>> 8);
    return (c ^ 0xffffffff) >>> 0;
}

/** MS-DOS-Zeit und -Datum (lokale Zeit, 2-Sekunden-Raster, ab 1980). */
function dosZeit(d: Date): { zeit: number; datum: number } {
    const jahr = Math.max(1980, d.getFullYear());
    return {
        zeit: (d.getHours() << 11) | (d.getMinutes() << 5) | Math.floor(d.getSeconds() / 2),
        datum: ((jahr - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate(),
    };
}

/**
 * Dateinamen fürs Archiv: ohne Pfad- und Windows-Sonderzeichen, nie leer, und
 * eindeutig — zwei „Rechnung.pdf" würden sich beim Entpacken überschreiben,
 * die zweite heißt deshalb „Rechnung (2).pdf".
 */
export function eindeutigeNamen(namen: string[]): string[] {
    const vergeben = new Set<string>();
    return namen.map((roh, i) => {
        const sauber =
            String(roh ?? "")
                // eslint-disable-next-line no-control-regex
                .replace(/[\\/:*?"<>|\u0000-\u001f]/g, "_")
                .replace(/^[.\s]+/, "")
                .trim() || `Dokument ${i + 1}`;
        let kandidat = sauber;
        const punkt = sauber.lastIndexOf(".");
        const stamm = punkt > 0 ? sauber.slice(0, punkt) : sauber;
        const endung = punkt > 0 ? sauber.slice(punkt) : "";
        for (let n = 2; vergeben.has(kandidat.toLowerCase()); n++) kandidat = `${stamm} (${n})${endung}`;
        vergeben.add(kandidat.toLowerCase());
        return kandidat;
    });
}

/** Baut das Archiv als Byte-Folge. */
export function baueZip(eintraege: ZipEintrag[]): Uint8Array {
    if (eintraege.length > 0xffff) throw new Error("Zu viele Dateien für ein ZIP-Archiv.");
    const enc = new TextEncoder();
    const teile: Uint8Array[] = [];
    const zentral: Uint8Array[] = [];
    let versatz = 0;

    for (const e of eintraege) {
        const name = enc.encode(e.name);
        const crc = crc32(e.daten);
        const groesse = e.daten.length;
        const { zeit, datum } = dosZeit(e.datum ?? new Date());
        if (versatz + 30 + name.length + groesse > 0xffffffff) {
            throw new Error("Das Archiv würde größer als 4 GB.");
        }

        const kopf = new Uint8Array(30 + name.length);
        const k = new DataView(kopf.buffer);
        k.setUint32(0, 0x04034b50, true);
        k.setUint16(4, 20, true); // benötigte Version 2.0
        k.setUint16(6, 0x0800, true); // Bit 11: Namen in UTF-8
        k.setUint16(8, 0, true); // gespeichert, nicht gepackt
        k.setUint16(10, zeit, true);
        k.setUint16(12, datum, true);
        k.setUint32(14, crc, true);
        k.setUint32(18, groesse, true);
        k.setUint32(22, groesse, true);
        k.setUint16(26, name.length, true);
        k.setUint16(28, 0, true);
        kopf.set(name, 30);

        const z = new Uint8Array(46 + name.length);
        const c = new DataView(z.buffer);
        c.setUint32(0, 0x02014b50, true);
        c.setUint16(4, 20, true);
        c.setUint16(6, 20, true);
        c.setUint16(8, 0x0800, true);
        c.setUint16(10, 0, true);
        c.setUint16(12, zeit, true);
        c.setUint16(14, datum, true);
        c.setUint32(16, crc, true);
        c.setUint32(20, groesse, true);
        c.setUint32(24, groesse, true);
        c.setUint16(28, name.length, true);
        // 30 Extra, 32 Kommentar, 34 Disk, 36 interne, 38 externe Attribute: 0
        c.setUint32(42, versatz, true);
        z.set(name, 46);

        teile.push(kopf, e.daten);
        zentral.push(z);
        versatz += kopf.length + groesse;
    }

    const zentralGroesse = zentral.reduce((s, z) => s + z.length, 0);
    const ende = new Uint8Array(22);
    const d = new DataView(ende.buffer);
    d.setUint32(0, 0x06054b50, true);
    d.setUint16(8, eintraege.length, true);
    d.setUint16(10, eintraege.length, true);
    d.setUint32(12, zentralGroesse, true);
    d.setUint32(16, versatz, true);

    const gesamt = new Uint8Array(versatz + zentralGroesse + ende.length);
    let pos = 0;
    for (const t of [...teile, ...zentral, ende]) {
        gesamt.set(t, pos);
        pos += t.length;
    }
    return gesamt;
}
